import type { AuthUser } from '@/lib/types';
import { formatAdminDateTimeLocalWithRelative } from '@/lib/datetime-display';

export type SettingsDraft = Record<string, string | boolean | number | null>;

export type SettingRecord = {
  setting_key: string;
  setting_value: string | number | boolean | null;
  setting_type: string;
  category: string;
  is_masked?: boolean;
  last_chars?: string;
};

export type ServiceStatusSlice = {
  lastActionAt?: string | null;
  lastActionStatus?: 'error' | 'success' | null;
  lastErrorAt?: string | null;
  lastErrorMessage?: string | null;
  lastSuccessAt?: string | null;
  aggregateStatus?: string;
  smtp2go?: ServiceStatusSlice;
  smtp?: ServiceStatusSlice;
};

export type LastServiceStatus = Record<string, ServiceStatusSlice>;

/** Section ids aligned with legacy `data-section` cards. */
export type SettingsSectionId =
  | 'openai'
  | 'scenarios'
  | 'chrome_extension'
  | 'zoom_phone'
  | 'immix'
  | 'periskope'
  | 'application'
  | 'regional'
  | 'templates'
  | 'monitoring'
  | 'maps'
  | 'holidays'
  | 'messaging'
  | 'branding'
  | 'compatibility_policy'
  | 'report_type_compatibility'
  | 'password_policy';

import {
  SETTINGS_SECTIONS_WITHOUT_DRAFT,
  SETTINGS_SECTION_META,
  SECTION_SETTING_KEYS,
} from '@/lib/settings-section-registry';

export { SETTINGS_SECTIONS_WITHOUT_DRAFT, SETTINGS_SECTION_META, SECTION_SETTING_KEYS };

/** Map legacy/alias hash fragments to canonical section ids. */
type SectionAliasMap = Readonly<Record<string, SettingsSectionId>>;

const SECTION_HASH_ALIASES: SectionAliasMap = {
  ai: 'openai',
  'operations-defaults': 'scenarios',
  'scenarios-delivery': 'scenarios',
  'report-type-compatibility': 'report_type_compatibility',
  templatessettingspanel: 'templates',
};

/** Resolve a hash fragment to a settings section id, or null when empty/unknown. */
export function resolveSettingsSectionFromHash(hashRaw?: string): SettingsSectionId | null {
  const hash = (hashRaw ?? (typeof window !== 'undefined' ? window.location.hash : ''))
    .replace(/^#/, '')
    .toLowerCase();
  if (!hash) return null;
  const canonical = SECTION_HASH_ALIASES[hash];
  if (canonical) return canonical;
  const byId = SETTINGS_SECTION_META.find((s) => s.id === hash);
  if (byId) return byId.id;
  const byCanonicalHash = SETTINGS_SECTION_META.find((s) => sectionHash(s.id) === hash);
  return byCanonicalHash?.id ?? null;
}

/** Read the initial section from `window.location.hash` synchronously.
 * Returns the matched section id, or null when the hash is empty (cold load: all collapsed).
 */
export function readInitialSettingsSection(): SettingsSectionId | null {
  return resolveSettingsSectionFromHash();
}

/** Canonical hash fragment for a section id (used in URL sync). */
export function sectionHash(id: SettingsSectionId): string {
  if (id === 'scenarios') return 'scenarios';
  if (id === 'templates') return 'templatesSettingsPanel';
  if (id === 'report_type_compatibility') return 'report-type-compatibility';
  return id;
}

export const SETTINGS_GROUP_META: {
  id: string;
  label: string;
  description: string;
}[] = [
  {
    id: 'AI & operations',
    label: 'AI & operations',
    description: 'Operator AI features, scenarios, and Chrome extension behavior',
  },
  {
    id: 'Integrations',
    label: 'Integrations',
    description: 'External systems Incident Reporter talks to',
  },
  {
    id: 'Platform',
    label: 'Platform',
    description: 'Branding, security, URLs, retention, and compatibility',
  },
  {
    id: 'Maps',
    label: 'Maps',
    description: 'Google Maps / geocoding keys',
  },
  {
    id: 'Scheduling',
    label: 'Scheduling',
    description: 'Holiday calendar sync',
  },
  {
    id: 'Messaging',
    label: 'Messaging',
    description: 'Email and SMS delivery providers',
  },
];

export function settingsGroupAnchorId(group: string): string {
  return `settings-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}


export const MONITORING_SETTING_KEYS = [
  'prometheus_query_url',
  'prometheus_rollup_timezone',
  'prometheus_query_timeout_ms',
  'prometheus_query_bearer_token',
] as const;

export const MONITORING_DEFAULTS: Record<string, string | null> = {
  prometheus_query_url: null,
  prometheus_rollup_timezone: null,
  prometheus_query_timeout_ms: '10000',
  prometheus_query_bearer_token: null,
};

export const CHROME_EXTENSION_BOOLEAN_KEYS = [
  'chrome_extension_request_support_enabled',
  'chrome_extension_grading_card_enabled',
  'chrome_extension_site_history_enabled',
  'chrome_extension_quick_actions_enabled',
] as const;

export const CHROME_EXTENSION_BOOLEAN_DEFAULTS: Record<string, boolean> = {
  chrome_extension_request_support_enabled: true,
  chrome_extension_grading_card_enabled: true,
  chrome_extension_site_history_enabled: true,
  chrome_extension_quick_actions_enabled: false,
};

/** Nationwide statutory holidays (stored as `FED` in DB and `holiday_provinces`). */
export const HOLIDAY_NATIONAL_REGION = 'FED';

export const HOLIDAY_NATIONAL = {
  code: HOLIDAY_NATIONAL_REGION,
  label: 'National',
} as const;

export const HOLIDAY_PROVINCES = [
  { code: 'AB', label: 'Alberta' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland & Labrador' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'ON', label: 'Ontario' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'QC', label: 'Quebec' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'YT', label: 'Yukon' },
] as const;

const HOLIDAY_REGION_LABEL_BY_CODE: Record<string, string> = {
  [HOLIDAY_NATIONAL_REGION]: HOLIDAY_NATIONAL.label,
  ...Object.fromEntries(HOLIDAY_PROVINCES.map((p) => [p.code, p.label])),
};

export function allHolidayRegionCodes(): string[] {
  return [HOLIDAY_NATIONAL_REGION, ...HOLIDAY_PROVINCES.map((p) => p.code)];
}

export function isAllHolidayRegionsSelected(selected: ReadonlySet<string>): boolean {
  return (
    selected.has(HOLIDAY_NATIONAL_REGION)
    && HOLIDAY_PROVINCES.every((p) => selected.has(p.code))
  );
}

/** Stable comma-separated value for `holiday_provinces` (national first, then provinces). */
export function serializeHolidayRegionSelection(codes: Iterable<string>): string {
  const selected = new Set(
    [...codes].map((c) => String(c).trim().toUpperCase()).filter(Boolean),
  );
  const parts: string[] = [];
  if (selected.has(HOLIDAY_NATIONAL_REGION)) parts.push(HOLIDAY_NATIONAL_REGION);
  for (const province of HOLIDAY_PROVINCES) {
    if (selected.has(province.code)) parts.push(province.code);
  }
  return parts.join(',');
}

/** Human-readable province/territory or federal label for a holiday `region` code. */
export function formatHolidayRegionLabel(region: string): string {
  const code = String(region ?? '').trim().toUpperCase();
  if (!code) return '';
  return HOLIDAY_REGION_LABEL_BY_CODE[code] ?? code;
}

/** Whether a synced holiday row should appear for the enabled province chip selection. */
export function holidayMatchesProvinceSelection(
  region: string,
  selectedProvinceCodes: ReadonlySet<string>,
): boolean {
  if (selectedProvinceCodes.size === 0) return false;
  const code = String(region ?? '').trim().toUpperCase();
  return selectedProvinceCodes.has(code);
}

export type HolidayPreviewRow = { date: string; name: string; region: string };

export type HolidaysByRegionGroup = {
  region: string;
  label: string;
  holidays: HolidayPreviewRow[];
};

/** Group upcoming holiday rows by scope (national first, then provinces in catalog order). */
export function groupHolidaysByRegion(
  holidays: HolidayPreviewRow[],
  selectedProvinceCodes: ReadonlySet<string>,
): HolidaysByRegionGroup[] {
  const byRegion = new Map<string, HolidayPreviewRow[]>();
  for (const row of holidays) {
    if (!holidayMatchesProvinceSelection(row.region, selectedProvinceCodes)) continue;
    const code = String(row.region ?? '').trim().toUpperCase();
    const bucket = byRegion.get(code);
    if (bucket) bucket.push(row);
    else byRegion.set(code, [row]);
  }
  return allHolidayRegionCodes()
    .filter((code) => selectedProvinceCodes.has(code) && byRegion.has(code))
    .map((code) => ({
      region: code,
      label: formatHolidayRegionLabel(code),
      holidays: (byRegion.get(code) ?? []).sort((a, b) =>
        String(a.date).slice(0, 10).localeCompare(String(b.date).slice(0, 10)),
      ),
    }));
}

const PASSWORD_KEYS = new Set([
  'openai_api_key',
  'immix_go_backdoor_api_key',
  'smtp2go_api_key',
  'sms_smtp2go_api_key',
  'smtp_password',
  'prometheus_query_bearer_token',
  'periskope_api_key',
  'google_maps_api_key',
  'google_static_maps_api_key',
  'google_geocoding_api_key',
  'google_places_api_key',
  'google_api_keys_viewer_service_account_private_key',
  'zoom_account_id',
  'zoom_client_id',
  'zoom_client_secret',
]);

const BOOLEAN_STORE_AS_ONE = new Set([
  'ai_summary_enabled',
  'ai_grading_enabled',
  'sop_ai_review_enabled',
  'email_smtp2go_enabled',
  'email_smtp_enabled',
  'use_smtp2go_api',
  'sms_enabled',
  'chrome_extension_request_support_enabled',
  'chrome_extension_grading_card_enabled',
  'chrome_extension_site_history_enabled',
  'chrome_extension_quick_actions_enabled',
  'zoom_phone_enabled',
  'periskope_enabled',
  'immix_go_api_enabled',
  'immix_go_report_media_auto_retrieval_enabled',
  'email_enabled',
  'smtp_secure',
  'password_policy_require_uppercase',
  'password_policy_require_lowercase',
  'password_policy_require_number',
  'password_policy_require_special',
]);

export function canViewSettings(user: AuthUser | null): boolean {
  return Boolean(user?.permissions?.includes('settings.view'));
}

export function canEditSettings(user: AuthUser | null): boolean {
  return Boolean(user?.permissions?.includes('settings.edit'));
}

export function canViewDeliveryRules(user: AuthUser | null): boolean {
  return Boolean(user?.permissions?.includes('delivery_rules.view'));
}

export function canEditDeliveryRules(user: AuthUser | null): boolean {
  return Boolean(user?.permissions?.includes('delivery_rules.edit'));
}

export function canViewSettingsSecrets(user: AuthUser | null): boolean {
  return Boolean(user?.permissions?.includes('settings.secrets.view'));
}

export function canViewServerAdmin(user: AuthUser | null): boolean {
  return Boolean(user?.permissions?.includes('server_admin.view'));
}

export function canOperateServerAdmin(user: AuthUser | null): boolean {
  return Boolean(
    user?.permissions?.includes('server_admin.operate') &&
      user?.permissions?.includes('server_admin.view'),
  );
}

export function parseSettingBool(value: unknown, defaultValue = false): boolean {
  if (value === null || value === undefined || value === '') return defaultValue;
  return value === '1' || value === 'true' || value === true || value === 1;
}

export function boolToStored(value: boolean): string {
  return value ? '1' : '0';
}

export function isPasswordKey(key: string): boolean {
  return PASSWORD_KEYS.has(key);
}

/**
 * Whether SMS messaging controls may render in Settings (Slice B).
 * Never gated on email `use_smtp2go_api` — SMS is an independent messaging capability.
 */
export function canRenderSmsMessagingControls(ctx: {
  messagingSectionAccessible: boolean;
  /** Intentionally unused: documents that email SMTP2GO preference must not hide SMS. */
  useSmtp2goApi?: boolean;
}): boolean {
  void ctx.useSmtp2goApi;
  return ctx.messagingSectionAccessible;
}

export function shouldStoreBoolAsOne(key: string): boolean {
  return BOOLEAN_STORE_AS_ONE.has(key);
}

export function maskedPlaceholder(record: SettingRecord | undefined): string {
  if (record?.is_masked && record.last_chars) {
    return `••••••••${record.last_chars}`;
  }
  return '•••••••• (leave blank to keep current)';
}

export function isMaskedDisplayValue(value: string): boolean {
  return value.startsWith('••••••••');
}

export function settingsForSection(
  all: SettingRecord[],
  sectionId: SettingsSectionId,
): SettingRecord[] {
  const explicit = SECTION_SETTING_KEYS[sectionId];
  if (explicit) {
    return explicit.map((key) => {
      const found = all.find((s) => s.setting_key === key);
      if (found) return found;
      if (sectionId === 'monitoring' && MONITORING_SETTING_KEYS.includes(key as (typeof MONITORING_SETTING_KEYS)[number])) {
        return synthesizeMonitoringSetting(key);
      }
      if (
        sectionId === 'chrome_extension'
        && CHROME_EXTENSION_BOOLEAN_KEYS.includes(key as (typeof CHROME_EXTENSION_BOOLEAN_KEYS)[number])
      ) {
        return synthesizeChromeExtensionSetting(key);
      }
      return {
        setting_key: key,
        setting_value: null,
        setting_type: 'string',
        category: sectionId,
      };
    });
  }
  if (sectionId === 'monitoring') {
    return MONITORING_SETTING_KEYS.map((key) => {
      const found = all.find((s) => s.setting_key === key);
      return found ?? synthesizeMonitoringSetting(key);
    });
  }
  return all.filter((s) => s.category === sectionId);
}

export function synthesizeChromeExtensionSetting(key: string): SettingRecord {
  const enabled = CHROME_EXTENSION_BOOLEAN_DEFAULTS[key] ?? false;
  return {
    setting_key: key,
    setting_value: enabled ? '1' : '0',
    setting_type: 'boolean',
    category: 'chrome_extension',
  };
}

export function synthesizeMonitoringSetting(key: string): SettingRecord {
  const base: SettingRecord = {
    setting_key: key,
    category: 'monitoring',
    is_masked: false,
    setting_value: MONITORING_DEFAULTS[key] ?? null,
    setting_type: 'string',
  };
  if (key === 'prometheus_query_timeout_ms') {
    return { ...base, setting_type: 'number', setting_value: MONITORING_DEFAULTS[key] };
  }
  if (key === 'prometheus_query_bearer_token') {
    return { ...base, setting_type: 'password', is_masked: true };
  }
  return base;
}

export function serviceStatusTitle(sectionKey: string, slice?: ServiceStatusSlice): string {
  if (!slice) return 'No status recorded';
  const lines: string[] = [];
  if (slice.lastActionAt) {
    lines.push(
      `Last action: ${slice.lastActionStatus === 'error' ? 'error' : 'success'} at ${formatAdminDateTimeLocalWithRelative(slice.lastActionAt)}`,
    );
  } else if (slice.lastSuccessAt) {
    lines.push(`Last success: ${formatAdminDateTimeLocalWithRelative(slice.lastSuccessAt)}`);
  } else {
    lines.push('Last action: none recorded');
  }
  if (slice.lastErrorAt) {
    lines.push(
      `Last error: ${formatAdminDateTimeLocalWithRelative(slice.lastErrorAt)}${slice.lastErrorMessage ? ` — ${slice.lastErrorMessage}` : ''}`,
    );
  } else {
    lines.push('Last error: never');
  }
  return lines.join('\n');
}

export function statusBadgeVariant(
  slice?: ServiceStatusSlice,
): 'default' | 'secondary' | 'destructive' {
  if (!slice?.lastActionAt && !slice?.lastErrorAt) return 'secondary';
  if (slice.lastActionStatus === 'error' || slice.aggregateStatus === 'error') return 'destructive';
  return 'default';
}
